import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, Sun, Moon } from "lucide-react";

const Students = ({ user }) => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [feeTypeFilter, setFeeTypeFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    gender: "male",
    class_id: "",
    guardian_name: "",
    guardian_phone: "",
    address: "",
    enrollment_date: new Date().toISOString().split("T")[0],
    session: "morning",
    fee_type: "full",
    discount_percentage: "0",
    fee_amount: "",
    transport_type: "guardian",
    assigned_bus_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Reset fee type filter for teachers (they shouldn't use it)
  useEffect(() => {
    if (user?.role === "teacher" && feeTypeFilter !== "all") {
      setFeeTypeFilter("all");
    }
  }, [user, feeTypeFilter]);

  const fetchData = async () => {
    try {
      // Always fetch students and classes
      const [studentsRes, classesRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/classes`),
      ]);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);
      
      // Fetch buses for all users (needed to display bus assignments)
      // But teachers won't see bus management features
      try {
        const busesRes = await axios.get(`${API}/buses`);
        setBuses(busesRes.data.filter(b => b.status === "active"));
      } catch (busError) {
        console.warn("Failed to load buses data:", busError);
        setBuses([]);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        discount_percentage: formData.fee_type === "discounted" ? parseFloat(formData.discount_percentage) : 0,
        fee_amount: parseFloat(formData.fee_amount),
        assigned_bus_id: formData.transport_type === "bus" ? formData.assigned_bus_id : null,
      };
      
      if (editingStudent) {
        await axios.put(`${API}/students/${editingStudent.id}`, submitData);
        toast.success("Student updated successfully");
      } else {
        await axios.post(`${API}/students`, submitData);
        toast.success("Student added successfully");
      }
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await axios.delete(`${API}/students/${id}`);
      toast.success("Student deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete student");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      dob: "",
      gender: "male",
      class_id: "",
      guardian_name: "",
      guardian_phone: "",
      address: "",
      enrollment_date: new Date().toISOString().split("T")[0],
      session: "morning",
      fee_type: "full",
      discount_percentage: "0",
      fee_amount: "",
      transport_type: "guardian",
      assigned_bus_id: "",
    });
    setEditingStudent(null);
  };

  const openEditDialog = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      dob: student.dob,
      gender: student.gender,
      class_id: student.class_id,
      guardian_name: student.guardian_name,
      guardian_phone: student.guardian_phone,
      address: student.address || "",
      enrollment_date: student.enrollment_date,
      session: student.session || "morning",
      fee_type: student.fee_type || "full",
      discount_percentage: (student.discount_percentage || 0).toString(),
      fee_amount: (student.fee_amount || 0).toString(),
      transport_type: student.transport_type || "guardian",
      assigned_bus_id: student.assigned_bus_id || "",
    });
    setIsDialogOpen(true);
  };

  const getFeeTypeBadge = (feeType, discountPercentage) => {
    if (feeType === "full") {
      return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">💰 Full</span>;
    } else if (feeType === "discounted") {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">💸 Discount ({discountPercentage}%)</span>;
    } else if (feeType === "endorsed") {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">🕌 Endorsed</span>;
    }
  };

  const getSessionBadge = (session) => {
    if (session === "morning") {
      return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium flex items-center gap-1"><Sun size={12} /> Morning</span>;
    } else {
      return <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium flex items-center gap-1"><Moon size={12} /> Afternoon</span>;
    }
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSession = sessionFilter === "all" || student.session === sessionFilter;
    const matchesFeeType = feeTypeFilter === "all" || student.fee_type === feeTypeFilter;
    return matchesSearch && matchesSession && matchesFeeType;
  });

  // Calculate summary statistics
  const totalStudents = students.length;
  const maleCount = students.filter(s => s.gender === "male").length;
  const femaleCount = students.filter(s => s.gender === "female").length;
  const morningCount = students.filter(s => s.session === "morning").length;
  const afternoonCount = students.filter(s => s.session === "afternoon").length;
  
  // Calculate breakdown statistics
  const maleMorning = students.filter(s => s.gender === "male" && s.session === "morning").length;
  const femaleMorning = students.filter(s => s.gender === "female" && s.session === "morning").length;
  const maleAfternoon = students.filter(s => s.gender === "male" && s.session === "afternoon").length;
  const femaleAfternoon = students.filter(s => s.gender === "female" && s.session === "afternoon").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1E624A]">Students Management</h2>
          <p className="text-sm text-gray-600">Maamulka Ardayda</p>
        </div>
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-student-btn"
                onClick={resetForm}
                className="bg-[#1E624A] hover:bg-[#164935]"
              >
                <Plus size={20} className="mr-2" />
                Add Student
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingStudent ? "Edit Student" : "Add New Student"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="student-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dob">Date of Birth *</Label>
                  <Input
                    id="dob"
                    type="date"
                    data-testid="student-dob-input"
                    value={formData.dob}
                    onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gender">Gender *</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger data-testid="student-gender-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="class">Class *</Label>
                  <Select
                    value={formData.class_id}
                    onValueChange={(value) => setFormData({ ...formData, class_id: value })}
                  >
                    <SelectTrigger data-testid="student-class-select">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map((cls) => (
                        <SelectItem key={cls.id} value={cls.id}>
                          {cls.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Session Selection */}
              <div>
                <Label className="mb-3 block">Session *</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="session"
                      value="morning"
                      checked={formData.session === "morning"}
                      onChange={(e) => setFormData({ ...formData, session: e.target.value })}
                      className="w-4 h-4 text-[#1E624A]"
                    />
                    <Sun size={18} className="text-orange-500" />
                    <span className="text-sm font-medium">Morning</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="session"
                      value="afternoon"
                      checked={formData.session === "afternoon"}
                      onChange={(e) => setFormData({ ...formData, session: e.target.value })}
                      className="w-4 h-4 text-[#1E624A]"
                    />
                    <Moon size={18} className="text-indigo-500" />
                    <span className="text-sm font-medium">Afternoon</span>
                  </label>
                </div>
              </div>

              {/* Fee Type Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fee_type">Fee Type *</Label>
                  <Select
                    value={formData.fee_type}
                    onValueChange={(value) => setFormData({ ...formData, fee_type: value, discount_percentage: value === "discounted" ? formData.discount_percentage : "0" })}
                  >
                    <SelectTrigger data-testid="student-fee-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">💰 Full</SelectItem>
                      <SelectItem value="discounted">💸 Discounted</SelectItem>
                      <SelectItem value="endorsed">🕌 Endorsed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="fee_amount">Fee Amount ($) *</Label>
                  <Input
                    id="fee_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    data-testid="student-fee-amount-input"
                    value={formData.fee_amount}
                    onChange={(e) => setFormData({ ...formData, fee_amount: e.target.value })}
                    placeholder="50.00"
                    required
                  />
                </div>
              </div>
              
              {formData.fee_type === "discounted" && (
                <div>
                  <Label htmlFor="discount_percentage">Discount Percentage (%) *</Label>
                  <Input
                    id="discount_percentage"
                    type="number"
                    min="0"
                    max="100"
                    data-testid="student-discount-input"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    placeholder="10"
                    required
                  />
                </div>
              )}

              {/* Transport Option */}
              <div>
                <Label className="mb-3 block">Transport Option *</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="transport"
                      value="guardian"
                      checked={formData.transport_type === "guardian"}
                      onChange={(e) => setFormData({ ...formData, transport_type: e.target.value, assigned_bus_id: "" })}
                      className="w-4 h-4 text-[#1E624A]"
                    />
                    <span className="text-2xl">👨‍👩‍👧‍👦</span>
                    <span className="text-sm font-medium">Guardian Transport</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="transport"
                      value="bus"
                      checked={formData.transport_type === "bus"}
                      onChange={(e) => setFormData({ ...formData, transport_type: e.target.value })}
                      className="w-4 h-4 text-[#1E624A]"
                    />
                    <span className="text-2xl">🚌</span>
                    <span className="text-sm font-medium">School Bus</span>
                  </label>
                </div>
              </div>

              {formData.transport_type === "bus" && (
                <div>
                  <Label htmlFor="assigned_bus">Assigned Bus *</Label>
                  <Select
                    value={formData.assigned_bus_id}
                    onValueChange={(value) => setFormData({ ...formData, assigned_bus_id: value })}
                  >
                    <SelectTrigger data-testid="student-bus-select">
                      <SelectValue placeholder="Select bus" />
                    </SelectTrigger>
                    <SelectContent>
                      {buses.map((bus) => (
                        <SelectItem key={bus.id} value={bus.id}>
                          {bus.bus_name} - {bus.area_served} ({bus.capacity} seats)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="guardian_name">Guardian Name *</Label>
                  <Input
                    id="guardian_name"
                    data-testid="student-guardian-name-input"
                    value={formData.guardian_name}
                    onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="guardian_phone">Guardian Phone *</Label>
                  <Input
                    id="guardian_phone"
                    data-testid="student-guardian-phone-input"
                    value={formData.guardian_phone}
                    onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="student-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="enrollment_date">Enrollment Date *</Label>
                <Input
                  id="enrollment_date"
                  type="date"
                  data-testid="student-enrollment-date-input"
                  value={formData.enrollment_date}
                  onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="cancel-student-btn"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-student-btn"
                  className="bg-[#1E624A] hover:bg-[#164935]"
                >
                  {editingStudent ? "Update" : "Add"} Student
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="compact-summary-grid">
        <div className="compact-summary-card">
          <span className="card-icon">👥</span>
          <div className="card-content">
            <span className="card-count">{totalStudents}</span>
            <div className="card-label">Total Students</div>
            <div className="card-sublabel">Wadarta Guud</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">👦</span>
          <div className="card-content">
            <span className="card-count">{maleCount}</span>
            <div className="card-label">Male Students</div>
            <div className="card-sublabel">Wiilal</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">👧</span>
          <div className="card-content">
            <span className="card-count">{femaleCount}</span>
            <div className="card-label">Female Students</div>
            <div className="card-sublabel">Gabdho</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">🌅</span>
          <div className="card-content">
            <span className="card-count">{morningCount}</span>
            <div className="card-label">Morning Session</div>
            <div className="card-sublabel">Fadhiga Subaxnimo</div>
          </div>
        </div>
        
        <div className="compact-summary-card">
          <span className="card-icon">🌇</span>
          <div className="card-content">
            <span className="card-count">{afternoonCount}</span>
            <div className="card-label">Afternoon Session</div>
            <div className="card-sublabel">Fadhiga Galabnimo</div>
          </div>
        </div>
      </div>

      {/* Breakdown Statistics */}
      <div className="breakdown-summary-grid">
        <div className="breakdown-summary-card">
          <span className="card-icon">👦</span>
          <div className="card-content">
            <span className="card-count">{maleMorning}</span>
            <div className="card-label">Morning Males</div>
          </div>
        </div>
        
        <div className="breakdown-summary-card">
          <span className="card-icon">👧</span>
          <div className="card-content">
            <span className="card-count">{femaleMorning}</span>
            <div className="card-label">Morning Females</div>
          </div>
        </div>
        
        <div className="breakdown-summary-card">
          <span className="card-icon">👦</span>
          <div className="card-content">
            <span className="card-count">{maleAfternoon}</span>
            <div className="card-label">Afternoon Males</div>
          </div>
        </div>
        
        <div className="breakdown-summary-card">
          <span className="card-icon">👧</span>
          <div className="card-content">
            <span className="card-count">{femaleAfternoon}</span>
            <div className="card-label">Afternoon Females</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-row">
        <div className="search-input relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            data-testid="search-students-input"
            placeholder="Search students by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="filter-group">
          <Label htmlFor="session-filter" className="filter-label">Filter by Session</Label>
          <Select value={sessionFilter} onValueChange={setSessionFilter}>
            <SelectTrigger data-testid="session-filter-select" className="filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sessions</SelectItem>
              <SelectItem value="morning">☀️ Morning</SelectItem>
              <SelectItem value="afternoon">🌇 Afternoon</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Fee Type filter only for admin/super_admin */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="filter-group">
            <Label htmlFor="fee-type-filter" className="filter-label">Filter by Fee Type</Label>
            <Select value={feeTypeFilter} onValueChange={setFeeTypeFilter}>
              <SelectTrigger data-testid="fee-type-filter-select" className="filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fee Types</SelectItem>
                <SelectItem value="full">💰 Full</SelectItem>
                <SelectItem value="discounted">💸 Discount</SelectItem>
                <SelectItem value="endorsed">🕌 Endorsed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Students Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStudents.map((student) => {
          const studentClass = classes.find((c) => c.id === student.class_id);
          return (
            <div
              key={student.id}
              data-testid={`student-card-${student.id}`}
              className="bg-white rounded-xl p-6 shadow-md card-hover"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{student.name}</h3>
                  <p className="text-sm text-gray-600">{studentClass?.name || "N/A"}</p>
                </div>
                {(user?.role === "admin" || user?.role === "super_admin") && (
                  <div className="flex gap-1">
                    <Button
                      data-testid={`edit-student-${student.id}-btn`}
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(student)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      data-testid={`delete-student-${student.id}-btn`}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(student.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Session:</span>
                  {getSessionBadge(student.session)}
                </div>
                {/* Finance fields only for admin/super_admin */}
                {(user?.role === "admin" || user?.role === "super_admin") && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Fee Type:</span>
                      {getFeeTypeBadge(student.fee_type, student.discount_percentage)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Fee Amount:</span>
                      <span className="text-sm font-semibold text-gray-900">${student.fee_amount?.toFixed(2) || "0.00"}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Transport:</span>
                  {student.transport_type === "bus" ? (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                      🚌 School Bus
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                      👨‍👩‍👧‍👦 Guardian
                    </span>
                  )}
                </div>
                {student.transport_type === "bus" && student.assigned_bus_id && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Bus:</span>
                    <span className="text-xs text-gray-700">
                      {buses.find(b => b.id === student.assigned_bus_id)?.bus_name || "Not Assigned"}
                    </span>
                  </div>
                )}
                <div className="text-sm text-gray-600 pt-2 border-t border-gray-200">
                  <p><strong>Guardian:</strong> {student.guardian_name}</p>
                  <p><strong>Phone:</strong> {student.guardian_phone}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredStudents.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          No students found
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-md text-center">
          <p className="text-2xl font-bold text-gray-900">{students.length}</p>
          <p className="text-sm text-gray-600">Total Students</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 shadow-md text-center">
          <p className="text-2xl font-bold text-orange-600">{students.filter(s => s.session === "morning").length}</p>
          <p className="text-sm text-gray-600">☀️ Morning</p>
        </div>
        <div className="bg-indigo-50 rounded-xl p-4 shadow-md text-center">
          <p className="text-2xl font-bold text-indigo-600">{students.filter(s => s.session === "afternoon").length}</p>
          <p className="text-sm text-gray-600">🌇 Afternoon</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 shadow-md text-center">
          <p className="text-2xl font-bold text-green-600">{filteredStudents.length}</p>
          <p className="text-sm text-gray-600">Filtered Results</p>
        </div>
      </div>
    </div>
  );
};

export default Students;
